// Records the storefront `meadow_id` (cremation case id) and `package` from the
// product-link URL params and makes them available to the rest of the flow.
//
// Two distinct sinks, for two distinct consumers:
//   1. Line-item PROPERTY `_meadow_id` on the urn — this is the ONLY value that
//      reaches the Shopify Carrier Calculated Shipping (rate-engine) callback in
//      `items[].properties`, and it enters Shopify's rate-response cache key.
//      The `_` prefix hides it from the customer in cart/checkout. Set on every
//      add-to-cart path (visible product form + AJAX/quick-add via fetch).
//   2. Cart ATTRIBUTES `meadow_id`/`package` + the package discount codes — the
//      pre-existing behavior, kept unchanged. Cart attributes do NOT reach the
//      rate callback, which is why (1) exists.
//
// `/cart/change.js` line-property updates (e.g. cart.js's final-disposition
// survey) replace a line's entire `properties` object rather than merging, so
// any such update made after add-to-cart silently erases `_meadow_id` unless we
// re-inject it here too.

(function () {
  const MEADOW_ID_PROPERTY = "_meadow_id";
  const PROPERTY_INPUT_NAME = `properties[${MEADOW_ID_PROPERTY}]`;
  const CART_ADD_PATH = "/cart/add";
  const CART_CHANGE_PATH = "/cart/change";

  function getMeadowId() {
    return sessionStorage.getItem("meadow_id");
  }

  // --- (1) line-item property: visible product forms ------------------------

  function setFormProperty(form, meadowId) {
    let input = form.querySelector(`input[name="${PROPERTY_INPUT_NAME}"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = PROPERTY_INPUT_NAME;
      form.appendChild(input);
    }
    input.value = meadowId;
  }

  function attachMeadowIdToProductForms(meadowId) {
    const selector = 'form[action*="/cart/add"]';
    document.querySelectorAll(selector).forEach((form) => {
      setFormProperty(form, meadowId);
    });

    // Cover forms rendered after load (quick-add modals, section reloads).
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches && node.matches(selector)) setFormProperty(node, meadowId);
          if (node.querySelectorAll) {
            node.querySelectorAll(selector).forEach((form) => {
              setFormProperty(form, meadowId);
            });
          }
        });
      });
    });
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // --- (1) line-item property: AJAX / quick-add via fetch -------------------

  function matchesSameOriginPath(url, path) {
    // Match only same-origin paths. A bare substring check would treat an
    // absolute third-party URL containing the path as a cart request and leak
    // the hidden case id into its body.
    if (typeof url !== "string") return false;
    try {
      const parsed = new URL(url, window.location.origin);
      return (
        parsed.origin === window.location.origin &&
        (parsed.pathname.endsWith(path) || parsed.pathname.endsWith(`${path}.js`))
      );
    } catch (_error) {
      return false;
    }
  }

  function isCartAddUrl(url) {
    return matchesSameOriginPath(url, CART_ADD_PATH);
  }

  function isCartChangeUrl(url) {
    return matchesSameOriginPath(url, CART_CHANGE_PATH);
  }

  function injectIntoFormData(formData, meadowId) {
    if (!formData.has(PROPERTY_INPUT_NAME)) {
      formData.append(PROPERTY_INPUT_NAME, meadowId);
    }
    return formData;
  }

  function injectIntoItem(item, meadowId) {
    item.properties = item.properties || {};
    if (!item.properties[MEADOW_ID_PROPERTY]) {
      item.properties[MEADOW_ID_PROPERTY] = meadowId;
    }
    return item;
  }

  function injectIntoJson(body, meadowId) {
    const payload = JSON.parse(body);
    if (Array.isArray(payload.items)) {
      payload.items = payload.items.map((item) => injectIntoItem(item, meadowId));
    } else if (payload.id !== undefined) {
      injectIntoItem(payload, meadowId);
    }
    return JSON.stringify(payload);
  }

  function injectIntoUrlEncodedString(body, meadowId) {
    const params = new URLSearchParams(body);
    if (!params.has(PROPERTY_INPUT_NAME)) {
      params.append(PROPERTY_INPUT_NAME, meadowId);
    }
    return params.toString();
  }

  function injectIntoStringBody(body, meadowId) {
    // Quick-add can post either JSON or form-encoded (URLSearchParams) strings.
    // Treating every string as JSON would throw on form-encoded bodies and drop
    // _meadow_id, so branch on the shape.
    const trimmed = body.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      return injectIntoJson(body, meadowId);
    }
    return injectIntoUrlEncodedString(body, meadowId);
  }

  function injectMeadowId(init, meadowId) {
    if (!init || init.body == null) return init;
    const body = init.body;
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      injectIntoFormData(body, meadowId);
    } else if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      if (!body.has(PROPERTY_INPUT_NAME)) body.append(PROPERTY_INPUT_NAME, meadowId);
    } else if (typeof body === "string") {
      init.body = injectIntoStringBody(body, meadowId);
    }
    return init;
  }

  function injectIntoCartChangeBody(body, meadowId) {
    // /cart/change.js's `properties` payload REPLACES the line's entire
    // properties object rather than merging, so re-add `_meadow_id` on every
    // such request or an unrelated property update (e.g. the final-disposition
    // survey) silently erases it.
    const payload = JSON.parse(body);
    if (payload && typeof payload.properties === "object" && payload.properties !== null) {
      if (!payload.properties[MEADOW_ID_PROPERTY]) {
        payload.properties[MEADOW_ID_PROPERTY] = meadowId;
      }
    }
    return JSON.stringify(payload);
  }

  function injectMeadowIdIntoCartChange(init, meadowId) {
    if (!init || typeof init.body !== "string") return init;
    try {
      init.body = injectIntoCartChangeBody(init.body, meadowId);
    } catch (_error) {
      // Not a JSON body we understand (e.g. a quantity-only update); leave as-is.
    }
    return init;
  }

  function interceptCartRequests() {
    if (window.__meadowCartAddPatched) return;
    window.__meadowCartAddPatched = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      try {
        const url = typeof input === "string" ? input : (input && input.url) || "";
        const meadowId = getMeadowId();
        // Only (url, init) calls can be safely rewritten; a Request object's
        // body is covered by the hidden-input path on the product form.
        if (meadowId && typeof input === "string") {
          if (isCartAddUrl(url)) {
            init = injectMeadowId(init || {}, meadowId);
          } else if (isCartChangeUrl(url)) {
            init = injectMeadowIdIntoCartChange(init || {}, meadowId);
          }
        }
      } catch (error) {
        console.error("Meadow: failed to attach _meadow_id to cart request", error);
      }
      return originalFetch(input, init);
    };
  }

  // --- (2) cart attributes + package discount codes (unchanged behavior) ----

  function syncCartAttributes(meadowId, packageType) {
    const updates = { attributes: {} };
    if (meadowId) updates.attributes.meadow_id = meadowId;
    if (packageType) updates.attributes.package = packageType;

    // These discount codes are unrelated to shipping; preserved as-is.
    if (packageType === "signature") {
      updates.discount = "MEADOWSIGNATURE";
    } else if (packageType === "basic") {
      updates.discount = "MEADOWBASIC";
    }

    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    }).catch((error) => console.error("Meadow: error updating cart:", error));
  }

  document.addEventListener("DOMContentLoaded", function () {
    const urlParams = new URLSearchParams(window.location.search);
    const meadowId = urlParams.get("meadow_id");
    const packageType = urlParams.get("package");

    if (meadowId) sessionStorage.setItem("meadow_id", meadowId);
    if (packageType) sessionStorage.setItem("package_type", packageType);

    const storedMeadowId = sessionStorage.getItem("meadow_id");
    const storedPackageType = sessionStorage.getItem("package_type");

    if (storedMeadowId) {
      attachMeadowIdToProductForms(storedMeadowId);
      interceptCartRequests();
    }

    if (storedMeadowId || storedPackageType) {
      syncCartAttributes(storedMeadowId, storedPackageType);
    }
  });
})();
